import React, { useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import {
  useGetAllPlaylistCategoriesQuery,
  useCreatePlaylistCategoryMutation,
  useUpdatePlaylistCategoryMutation,
  useDeletePlaylistCategoryMutation,
} from "../../services/modules/playlistCategory";
import { useUpdatePlaylistIsHomeMutation } from "../../services/modules/playlist";
import { useDispatch, useSelector } from "react-redux";
import {
  setSelectedPlaylistCategory,
  clearSelectedPlaylistCategory,
  togglePlaylistCategoryForm,
} from "../../store/playlistCategories";
import {
  DndContext,
  closestCenter,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const PlaylistCategoryList = () => {
  const dispatch = useDispatch();
  const selectedPlaylistCategory = useSelector(
    (state) => state.playlistCategories.selectedPlaylistCategory
  );
  const isFormOpen = useSelector((state) => state.playlistCategories.isFormOpen);

  const { data, isLoading, refetch } = useGetAllPlaylistCategoriesQuery();
  const [createPlaylistCategory] = useCreatePlaylistCategoryMutation();
  const [updatePlaylistCategory] = useUpdatePlaylistCategoryMutation();
  const [deletePlaylistCategory] = useDeletePlaylistCategoryMutation();
  const [updatePlaylistIsHome] = useUpdatePlaylistIsHomeMutation();

  const playlistCategories = data?.data?.result || [];
  const [orderedCategories, setOrderedCategories] = useState([]);

  const [form, setForm] = useState({ name: "", coverImage: "" });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  // ORDINE DRAG&DROP
  useEffect(() => {
    setOrderedCategories((prev) => {
      const newOrdered = [...playlistCategories].sort((a, b) => (a.order || 0) - (b.order || 0));
      if (JSON.stringify(prev.map(x=>x.id)) !== JSON.stringify(newOrdered.map(x=>x.id))) {
        return newOrdered;
      }
      return prev;
    });
  }, [playlistCategories]);

  useEffect(() => {
    if (selectedPlaylistCategory) {
      setForm({
        name: selectedPlaylistCategory.name || "",
        coverImage: selectedPlaylistCategory.coverImage || "",
      });
    } else {
      setForm({ name: "", coverImage: "" });
    }
  }, [selectedPlaylistCategory]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const openEditForm = (cat) => {
    dispatch(setSelectedPlaylistCategory(cat));
    if (!isFormOpen) dispatch(togglePlaylistCategoryForm());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmedName = form.name.trim();

    try {
      if (selectedPlaylistCategory) {
        await updatePlaylistCategory({
          id: selectedPlaylistCategory.id,
          name: trimmedName,
          coverImage: form.coverImage,
        }).unwrap();
        toast.success("Categoria playlist aggiornata!");
      } else {
        await createPlaylistCategory({
          name: trimmedName,
          coverImage: form.coverImage,
        }).unwrap();
        toast.success("Categoria playlist creata!");
      }

      dispatch(clearSelectedPlaylistCategory());
      dispatch(togglePlaylistCategoryForm());
      setForm({ name: "", coverImage: "" });
      await refetch();
    } catch (err) {
      console.error("Errore salvataggio:", err);
      toast.error("Impossibile salvare la categoria playlist.");
    }
  };

  const handleDelete = async () => {
    try {
      await deletePlaylistCategory(deleteId).unwrap();
      toast.success("Categoria playlist cancellata!");
      setDeleteId(null);
      setShowDeleteModal(false);
      await refetch();
    } catch (err) {
      toast.error("Errore durante l'eliminazione.");
      console.error("Errore eliminazione:", err);
    }
  };

 const toggleHomepage = async (cat) => {
  try {
    await updatePlaylistIsHome({ id: cat.id, isHome: !cat.isHome }).unwrap();
    // Aggiorna subito lo stato locale
    setOrderedCategories(prev =>
      prev.map(c =>
        c.id === cat.id ? { ...c, isHome: !cat.isHome } : c
      )
    );
    toast.success("Stato homepage aggiornato!");
    // (mantieni comunque il refetch in caso di altri update)
    await refetch();
  } catch (err) {
    toast.error("Errore aggiornamento homepage.");
    console.error(err);
  }
};

  const cancelForm = () => {
    setForm({ name: "", coverImage: "" });
    dispatch(clearSelectedPlaylistCategory());
    dispatch(togglePlaylistCategoryForm());
  };

  // ---- DRAG & DROP ----
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = orderedCategories.findIndex((cat) => cat.id.toString() === active.id);
    const newIndex = orderedCategories.findIndex((cat) => cat.id.toString() === over.id);

    const newOrdered = arrayMove(orderedCategories, oldIndex, newIndex);
    setOrderedCategories(newOrdered);
  };

  // Salva ordine backend
  const saveOrder = async () => {
    try {
      await Promise.all(
        orderedCategories.map((cat, idx) =>
          updatePlaylistCategory({
            id: cat.id,
            name: cat.name,
            coverImage: cat.coverImage,
            order: idx + 1,
          }).unwrap()
        )
      );
      toast.success("Ordine salvato!");
      await refetch();
    } catch (err) {
      toast.error("Errore nel salvataggio dell'ordine");
    }
  };

  return (
    <div className="p-4">
      <ToastContainer position="top-right" autoClose={3000} style={{ zIndex: 9999 }} />
      <h2 className="text-xl font-bold mb-6">Categorie Playlist</h2>

      {isLoading ? (
        <p>Loading playlist categories...</p>
      ) : orderedCategories.length > 0 ? (
        <>
          <DndContext
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <table className="min-w-full bg-white border text-sm">
              <thead className="bg-gray-100">
                <tr>
                  {/* <th className="border px-2 py-1 w-[80px]">Ordina</th> */}
                  <th className="border px-2 py-1">ID</th>
                  <th className="border px-2 py-1">Descrizione</th>
                  <th className="border px-2 py-1">Copertina</th>
                  <th className="border px-2 py-1">Azione</th>
                </tr>
              </thead>
              <SortableContext
                items={orderedCategories.map((cat) => cat.id.toString())}
                strategy={verticalListSortingStrategy}
              >
                <tbody>
                  {orderedCategories.map((cat) => (
                    <SortableRow
                      key={cat.id}
                      cat={cat}
                      openEditForm={openEditForm}
                      setDeleteId={setDeleteId}
                      setShowDeleteModal={setShowDeleteModal}
                      toggleHomepage={toggleHomepage}
                    />
                  ))}
                </tbody>
              </SortableContext>
            </table>
          </DndContext>
          <div className="flex justify-end mt-3">
            {/* <button
              onClick={saveOrder}
              className="bg-red-600 text-white px-4 py-2 rounded"
            >
              Salva Ordine
            </button> */}
          </div>
        </>
      ) : (
        <p className="text-gray-500">No categorie playlist trovate.</p>
      )}

      {isFormOpen ? (
        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <input
            type="text"
            name="name"
            placeholder="Playlist Nome Categoria"
            value={form.name}
            onChange={handleInputChange}
            className="border p-2 w-full rounded"
            required
          />
          <input
            type="text"
            name="coverImage"
            placeholder="URL immagine di copertina"
            value={form.coverImage}
            onChange={handleInputChange}
            className="border p-2 w-full rounded"
          />

          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={cancelForm}
              className="px-4 py-2 bg-gray-300 rounded"
            >
              Annulla
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-red-600 text-white rounded"
            >
              {selectedPlaylistCategory ? "Update" : "Crea"}
            </button>
          </div>
        </form>
      ) : (
        <div className="flex justify-end mt-6">
          <button
            onClick={() => dispatch(togglePlaylistCategoryForm())}
            className="bg-gray-800 text-white px-5 py-2 rounded"
          >
            Aggiungi Nuova Categoria Playlist
          </button>
        </div>
      )}

      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded p-6 shadow-md text-center space-y-4 w-96">
            <h3 className="text-lg font-semibold">Conferma Eliminazione</h3>
            <p>Sei sicuro che vuoi cancellare questa categoria playlist?</p>
            <div className="flex justify-center gap-4">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteId(null);
                }}
                className="px-4 py-2 rounded bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 rounded bg-red-500 text-white"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlaylistCategoryList;

// --- SORTABLE ROW ---

const SortableRow = ({
  cat,
  openEditForm,
  setDeleteId,
  setShowDeleteModal,
  toggleHomepage,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: cat.id.toString() });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <tr ref={setNodeRef} style={style} className="cursor-grab hover:bg-gray-50">
      {/* <td className="border px-2 py-1" {...attributes} {...listeners}>
        <svg width="24" height="24" className="inline" fill="gray"><circle cx="12" cy="12" r="10" /></svg>
      </td> */}
      <td className="border px-2 py-1">{cat.id}</td>
      <td className="border px-2 py-1">{cat.name || "-"}</td>
      <td className="border px-2 py-1 w-[300px]">
        {cat.coverImage ? (
          <img
            src={cat.coverImage}
            alt="cover"
            className="w-12 h-12 object-cover rounded"
          />
        ) : "-"}
      </td>
      <td className="border px-2 py-1 text-left w-[300px] overflow-hidden">
        <div className="space-x-2 flex flex-wrap items-center">
          <button
            onClick={() => {
              setDeleteId(cat.id);
              setShowDeleteModal(true);
            }}
            className="px-3 py-1 bg-gray-100 border border-gray-300 rounded"
          >
            Elimina
          </button>
          <button
            onClick={() => openEditForm(cat)}
            className="px-3 py-1 bg-gray-100 border border-gray-300 rounded"
          >
            Modifica
          </button>
          {/* <input
            type="checkbox"
            checked={cat.isHome === true}
            onChange={() => toggleHomepage(cat)}
            className="h-4 w-4 ml-2"
            title="Mostra in homepage"
          />
          <label className="ml-1">Homepage</label> */}
        </div>
      </td>
    </tr>
  );
};
